import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { TreeViewBaseItem } from "@mui/x-tree-view-pro";
import { PositionStatusEnum } from "@sinch/core/entities/serverEnums";
import { groupBy, includes, values } from "ramda";
import { isNilOrEmpty, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React from "react";
import { JobName } from "@sinch/components/application/Job/components/JobName/JobName";
import { PositionName } from "@sinch/components/application/Position/components/PositionName/PositionName";
import { ConceptPill } from "@sinch/components/application/Position/Status/ConceptPill";
import { QuotePill } from "@sinch/components/application/Position/Status/QuotePill";
import {
  JobNameFragment,
  JobRatelistsFragment,
  JobsRatelistsQuery,
  PositionNameFragment,
  PositionRatelistsFragment,
  ShiftNameFragment,
  TransportRatelistsQuery,
  TransportTripRatelistsFragment,
  TripBaseFragment,
} from "@sinch/components/application/Rate/RateMigration/queries.types";
import { ExtendedTreeItemProps } from "@sinch/components/application/Rate/RateMigration/types";
import { ShiftName } from "@sinch/components/application/Shift/components/ShiftName/ShiftName";
import { TransportName } from "@sinch/components/application/Transport/TransportName/TransportName";
import { useTransportDirection } from "@sinch/components/application/Transport/useTransportDirection";

export const useRateMigrationTree = () => {
  const { getTransportDirectionLabel } = useTransportDirection();

  const buildTransportRateMigrationTree = (
    field: "priceRatelist" | "wageRatelist",
    data?: Pick<TransportRatelistsQuery, "transportTrips">
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (isNilOrEmpty(data?.transportTrips)) {
      return [];
    }
    const tripsByJob = groupBy<TransportRatelistsQuery["transportTrips"][0]>(
      ({ shift }) => shift.job.id,
      data?.transportTrips?.filter(({ shift }) => shift.maxStatus !== PositionStatusEnum.Completed) ?? []
    );

    return values(tripsByJob).map((trip) => ({
      ...getJobRow(trip[0].shift.job, field, false, false),
      children: values(groupBy(({ shift }) => shift.id, trip)).map((trip) => ({
        ...getShiftRow(trip[0].shift),
        children: trip
          .filter((item) => isNotNil(item[field]))
          .map((pos: TripBaseFragment & TransportTripRatelistsFragment) => getTripRow(pos, field)),
      })),
    }));
  };

  /**
   * Generates a tree structure for rate migration data from job-related input.
   *
   * The function processes the provided `data` to build a tree-like structure:
   * 1. Filters jobs containing a non-null ratelist.
   * 2. Maps each job to tree-compatible objects.
   *
   */
  const buildRateMigrationTree = (
    field: "priceRatelist" | "wageRatelist",
    data?: Pick<JobsRatelistsQuery, "jobs" | "positions">,
    allowedSourceRatelists: string[] = []
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    if (isNilOrEmpty(data?.jobs) && isNilOrEmpty(data?.positions)) {
      return [];
    }
    const positionsByJob = groupBy<JobsRatelistsQuery["positions"][0]>(
      ({ shift }) => shift.job.id,
      data?.positions ?? []
    );

    const positionsRatelistsTree = values(positionsByJob).map((positions) => ({
      ...getJobRow(
        positions[0].shift.job,
        field,
        includes(positions[0].shift.job[field]?.id, allowedSourceRatelists) &&
          !positions[0].shift.job.hasClosedPositions
      ),
      children: values(groupBy(({ shift }) => shift.id, positions)).map((position) => ({
        ...getShiftRow(position[0].shift),
        children: position
          .filter((item) => isNotNil(item[field]))
          .map((pos: PositionNameFragment & PositionRatelistsFragment) => getPositionRow(pos, field)),
      })),
    }));
    const jobIds = positionsRatelistsTree.map(({ entity }) => entity.id);
    const jobsWithDefaultRatelist =
      data?.jobs
        .filter((item) => isNotNil(item[field]) && !includes(item.id, jobIds))
        .map((job) => ({
          ...getJobRow(job, field, !job.hasClosedPositions),
        })) ?? [];

    return [...jobsWithDefaultRatelist, ...positionsRatelistsTree];
  };

  /**
   * Transforms job-related data from the given fragments into the required job row structure.
   */
  const getJobRow = (
    { id, name, minBeginning, order, ...job }: JobNameFragment & JobRatelistsFragment,
    field: "priceRatelist" | "wageRatelist",
    selectable: boolean = true,
    canMigrate: boolean = true
  ): ExtendedTreeItemProps => ({
    id: `Job:${id}`,
    entity: { type: "Job" as const, id },
    label: name,
    selectable: selectable,
    canMigrate: canMigrate,
    renderLabel: (
      <Stack alignItems="center" direction="row" spacing={1}>
        <JobName
          beginning={minBeginning}
          id={id}
          name={<Typography variant="subtitle1">{`${order.name} - ${name}`}</Typography>}
          orderNumber={order.number}
        />
        {order.quote && <QuotePill />}
      </Stack>
    ),
    ratelist: { id: job[field]!.id, name: job[field]!.name, version: job[field]!.version },
  });

  /**
   * Generates a structured object representing a shift row with detailed data and rendering information.
   */
  const getShiftRow = ({ id, name }: ShiftNameFragment): ExtendedTreeItemProps => ({
    id: `Shift:${id}`,
    entity: { type: "Shift" as const, id: id },
    label: name,
    selectable: false,
    renderLabel: <ShiftName id={id} name={name} />,
    ratelist: null,
  });

  /**
   * Transforms a composite position fragment object into a structured row object with specific properties.
   */
  const getTripRow = (
    trip: TripBaseFragment & TransportTripRatelistsFragment,
    field: "priceRatelist" | "wageRatelist"
  ): ExtendedTreeItemProps => ({
    id: `TransportTrip:${trip.id}`,
    entity: { type: "TransportTrip" as const, id: trip.id },
    label: getTransportDirectionLabel(trip.direction) ?? "",
    selectable: true,
    canMigrate: true,
    renderLabel: (
      <TransportName
        beginning={trip.beginning}
        direction={trip.direction}
        end={trip.end}
        id={trip.id}
        place={trip.place}
      />
    ),
    ratelist: {
      id: trip[field]!.id,
      name: trip[field]!.name,
      version: trip[field]!.version,
    },
  });

  /**
   * Transforms a composite position fragment object into a structured row object with specific properties.
   */
  const getPositionRow = (
    pos: PositionNameFragment & PositionRatelistsFragment,
    field: "priceRatelist" | "wageRatelist"
  ): ExtendedTreeItemProps => ({
    id: `Position:${pos.id}`,
    entity: { type: "Position" as const, id: pos.id },
    label: pos.name ?? "",
    selectable: true,
    canMigrate: true,
    renderLabel: (
      <Stack alignItems="center" direction="row" spacing={1}>
        <PositionName
          beginning={pos.beginning}
          capacity={pos.size}
          end={pos.end}
          id={pos.id}
          name={[pos.profession.name, pos.name].filter(isNotNilOrEmpty).join(" - ")}
          role={pos.role}
          staffCount={pos.attendances.length}
          variant="regularLine"
        />
        {pos.concept && <ConceptPill />}
      </Stack>
    ),
    ratelist: {
      id: pos[field]!.id,
      name: pos[field]!.name,
      version: pos[field]!.version,
    },
  });

  return { buildTransportRateMigrationTree, buildRateMigrationTree };
};
/**
 * Flattens a nested tree structure into a single array of items.
 */
export const flattenTree = (tree: TreeViewBaseItem<ExtendedTreeItemProps>[]): ExtendedTreeItemProps[] => {
  let result: ExtendedTreeItemProps[] = [];
  for (const node of tree) {
    result = [
      ...result,
      node as unknown as ExtendedTreeItemProps,
      ...(node.children ? flattenTree(node.children as TreeViewBaseItem<ExtendedTreeItemProps>[]) : []),
    ];
  }
  return result;
};
